import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	ViewChild,
	afterNextRender,
	computed,
	input,
	signal
} from '@angular/core';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { stringHelper } from 'lib/helpers/string-helper.class.js';
import { DialogService } from 'lib/ui/dialog/index.js';
import { FormFieldComponent } from '../form-field.component.js';

@Component({
	selector: 'ui-text-area-form-field',
	templateUrl: './text-area-form-field.component.html',
	styleUrls: ['../form-fields.shared.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class TextAreaFormFieldComponent extends FormFieldComponent<string> {
	public readonly maxLength = input<number>();
	public readonly validation = input<'email'>();
	public readonly focusOnField = input<boolean>(false);
	public readonly enableEmoji = input<boolean>(false);
	public readonly enableGenAi = input<boolean>(true);
	public readonly genAiClientContext = input<UserDb.IUserItem | undefined>(undefined);
	public readonly height = input<'default' | 'large'>();

	public readonly enableEmojiCalculated = computed<boolean>(() => {
		if (!this.enableEmoji()) {
			return false;
		}
		if (this.isBrowserPlatform()) {
			return true;
		}
		return false;
	});

	public readonly enableSnippetCalculated = computed<boolean>(() => {
		// Only trainers can use  snippets
		if (this.authUser()?.userType === 'client') {
			return false;
		}
		return true;
	});

	public readonly enableGenAiCalculated = computed<boolean>(() => {
		if (!this.enableGenAi()) {
			return false;
		}

		// Only trainers can use AI assistant
		if (this.authUser()?.userType === 'client') {
			return false;
		}
		return true;
	});

	public readonly showActionBar = computed<boolean>(() => {
		return this.enableEmojiCalculated() || this.enableGenAiCalculated() || this.enableSnippetCalculated();
	});

	public readonly showEmojiSelector = signal<boolean>(false);

	@ViewChild('input') readonly inputElement?: ElementRef;

	constructor(private readonly dialogService: DialogService) {
		super();

		afterNextRender(() => {
			if (this.focusOnField() && this.canFocusField() && this.inputElement) {
				this.inputElement.nativeElement.focus();
			}
		});
	}

	handleSelectEmoji(emojiUnicode: string): void {
		if (this.inputElement?.nativeElement) {
			const newValue: string = `${this.inputElement.nativeElement.value}${emojiUnicode}`;

			this.inputElement.nativeElement.value = newValue;
			this.updateValue(newValue);
		}
	}

	handleModelChange(value?: string): void {
		super.updateValue(value);
	}

	handleAskAi(): void {
		this.dialogService.openGenAiDialog({
			clientContext: this.genAiClientContext()
		});
	}

	handleSelectSnippet(): void {
		this.dialogService.openSelectSnippetDialog({
			select: (item) => {
				this.updateValue(item.text);
			}
		});
	}

	protected getHint(value?: string): string | undefined {
		const maxLength = this.maxLength();

		if (maxLength && value && value?.length === maxLength) {
			return `Max length reached (${maxLength} chars)`;
		}

		if (value && this.validation() === 'email' && !stringHelper.isValidEmail(value ?? '')) {
			return `Invalid e-mail`;
		}

		return undefined;
	}

	protected isValueValid(value?: string): boolean {
		const maxLength = this.maxLength();

		if (maxLength) {
			if (!value) {
				return false;
			}
			if (value.length > maxLength) {
				return false;
			}
		}

		if (this.validation() === 'email') {
			return stringHelper.isValidEmail(value ?? '');
		}

		return true;
	}
}
